import type { PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select } from 'redux-saga/effects';

import { setContractVerificationState } from '~features/contract-deployer/contract-deployer.slice';
import {
  getImageUploadTypeForStreamEvent,
  updateProjectImagePath,
} from '~features/project-config/project-config.helpers';
import { selectProjectConfig, selectProjectId } from '~features/project-config/project-config.selectors';
import {
  cancelProjectConfigLoad,
  deleteGalleryImage,
  deleteGalleryImageError,
  deleteGalleryImageSuccess,
  getProjectConfig,
  setProjectConfig,
  setProjectConfigError,
  updateProjectConfig,
  updateProjectConfigError,
  updateProjectConfigSuccess,
  updateProjectContractConfig,
  updateProjectDappConfig,
  uploadGalleryImage,
  uploadGalleryImageSuccess,
  uploadImage,
  uploadImageError,
  uploadImageSuccess,
} from '~features/project-config/project-config.slice';
import { subscribeChannels } from '~features/pubsub/pubsub.slice';
import {
  addStreamActivity,
  initProjectStream,
  resetProjectStreamData,
  unsubscribeFromProjectStream,
} from '~features/streams/streams.slice';
import { fetchProtectedAPI, putProtectedAPI } from '~features/utils/api/api.sagas';
import { doesFileExistsOnS3, uploadFileToS3 } from '~features/utils/s3storage/s3storage.sagas';
import { takeEveryWithMonitoring, takeLatestWithMonitoring } from '~features/utils/saga-utils';
import { initWeb3Config } from '~features/web3/web3.slice';
import type { ContractType, DappSettingsType, ProjectType } from '~types/ProjectType';
import type { CakeActivityType } from '~types/Streams';
import getS3RootProjectPath from '~utils/api/getS3RootProjectPath';

window.Buffer = window.Buffer || Buffer;

function* fetchProjectSaga(action: PayloadAction<{ id: string; force: boolean }>): Iterator<any> {
  const { id, force } = action.payload;
  const currentId: string = yield select(selectProjectId);

  if (!force && id === currentId) {
    yield put(cancelProjectConfigLoad());
    return;
  }

  try {
    const path = `project/${id}`;
    const res: Response = yield call(fetchProtectedAPI, path);
    const data: ProjectType = yield res.json();

    if (data) {
      yield put(setProjectConfig(data));
      yield put(initWeb3Config(data.contract.abi));
      yield put(
        setContractVerificationState({
          testnet: data.contract.testnet.isVerified,
          livenet: data.contract.livenet.isVerified,
        }),
      );
      yield put(subscribeChannels([`project_${data._id}`]));
      yield put(unsubscribeFromProjectStream());
      yield put(resetProjectStreamData());
      yield put(initProjectStream());
    } else {
      yield put(setProjectConfigError('Project not found'));
    }
  } catch (e) {
    console.log(e);
    yield put(setProjectConfigError(e.toString()));
  }
}

function* updateProjectSaga(action: PayloadAction<ProjectType>) {
  const currentProjectConfig = yield select(selectProjectConfig);
  const updatedProjectConfig = {
    ...currentProjectConfig,
    ...action.payload,
  };

  const path = `project/${currentProjectConfig._id}`;
  try {
    const res: Response = yield call(putProtectedAPI, path, updatedProjectConfig);

    if (res.status === 200) {
      const data: ProjectType = yield res.json();
      yield put(updateProjectConfigSuccess({ ...data, _id: currentProjectConfig._id }));
    } else {
      yield put(updateProjectConfigError(`Update failed with status code ${res.status}`));
    }
  } catch (e) {
    yield put(updateProjectConfigError(e.toString()));
  }
}

function* updateProjectContractSaga(action: PayloadAction<{ contract: ContractType; event: CakeActivityType }>) {
  const currentProjectConfig = yield select(selectProjectConfig);
  const updatedProjectConfig = {
    ...currentProjectConfig,
    contract: action.payload.contract,
  };
  yield call(updateProjectSaga, updateProjectConfig(updatedProjectConfig));
}

function* updateProjectDappSaga(action: PayloadAction<{ dapp: DappSettingsType; event?: CakeActivityType }>) {
  const currentProjectConfig = yield select(selectProjectConfig);
  const updatedProjectConfig = {
    ...currentProjectConfig,
    dapp: action.payload.dapp,
  };
  yield call(updateProjectSaga, updateProjectConfig(updatedProjectConfig));

  if (action.payload.event) {
    yield put(
      addStreamActivity({
        verb: 'cakeConfig',
        object: {
          ...action.payload.event,
        },
        projectId: currentProjectConfig._id,
      }),
    );
  }
}

function* uploadImageSaga(action: PayloadAction<{ file: File; key: string }>) {
  const { file, key } = action.payload;
  const project: ProjectType = yield select(selectProjectConfig);

  try {
    const fileLocation: {
      dir: string;
      path: string;
    } = getS3RootProjectPath(project.name);
    const data = yield uploadFileToS3(file, fileLocation.dir);

    const updatedDappSettings: DappSettingsType = updateProjectImagePath(project.dapp, data.location, key);
    const updatedProject = {
      ...project,
      dapp: updatedDappSettings,
    };

    yield call(updateProjectSaga, updateProjectConfig(updatedProject));
    yield put(
      addStreamActivity({
        verb: 'cakeConfig',
        object: {
          content: {
            event: `${getImageUploadTypeForStreamEvent(key)}`,
          },
          media: data.location,
        },
        projectId: project._id,
      }),
    );
    yield put(uploadImageSuccess(key));
  } catch (err) {
    yield put(uploadImageError({ key, error: err.toString() }));
  }
}

function* uploadGalleryImageSaga(action: PayloadAction<{ file: File; key: string }>): Iterator<any> {
  const { file, key } = action.payload;

  const project: ProjectType = yield select(selectProjectConfig);
  const fileLocation: {
    dir: string;
    path: string;
  } = getS3RootProjectPath(project.name);

  const isFile: boolean = yield doesFileExistsOnS3(file.name, fileLocation.path);

  if (isFile) {
    const updatedProject: ProjectType = {
      ...project,
      dapp: {
        ...project.dapp,
        images: {
          ...project.dapp.images,
          gallery: [...project.dapp.images.gallery, `${fileLocation.path}/${file.name}`],
        },
      },
    };
    yield call(updateProjectSaga, updateProjectConfig(updatedProject));
  } else {
    const data: any = yield uploadFileToS3(file, fileLocation.dir);
    const updatedProject: ProjectType = {
      ...project,
      dapp: {
        ...project.dapp,
        images: {
          ...project.dapp.images,
          gallery: [...project.dapp.images.gallery, data.location],
        },
      },
    };
    yield call(updateProjectSaga, updateProjectConfig(updatedProject));
    yield put(uploadGalleryImageSuccess({ path: data.location, key }));
  }
}

function* deleteGalleryImageSaga(action: PayloadAction<number>) {
  const index = action.payload;
  const project: ProjectType = yield select(selectProjectConfig);
  const galleryImages = [...project.dapp.images.gallery];
  galleryImages.splice(index, 1);

  const updatedProject: ProjectType = {
    ...project,
    dapp: {
      ...project.dapp,
      images: {
        ...project.dapp.images,
        gallery: galleryImages,
      },
    },
  };
  try {
    yield call(updateProjectSaga, updateProjectConfig(updatedProject));
    yield put(deleteGalleryImageSuccess());
  } catch (e) {
    yield put(deleteGalleryImageError());
  }
}

export default function* campaignSaga(): Iterator<any> {
  yield all([
    yield takeLatestWithMonitoring(getProjectConfig.type, fetchProjectSaga, 'project.fetch'),
    yield takeLatestWithMonitoring(updateProjectConfig.type, updateProjectSaga, 'project.update'),
    yield takeLatestWithMonitoring(
      updateProjectContractConfig.type,
      updateProjectContractSaga,
      'project.contract.update',
    ),
    yield takeLatestWithMonitoring(updateProjectDappConfig.type, updateProjectDappSaga, 'project.dapp.update'),
    yield takeLatestWithMonitoring(uploadImage.type, uploadImageSaga, 'project.image.upload'),
    yield takeLatestWithMonitoring(uploadGalleryImage.type, uploadGalleryImageSaga, 'project.gallery-image.upload'),
    yield takeEveryWithMonitoring(deleteGalleryImage.type, deleteGalleryImageSaga, 'project.gallery-image.delete'),
  ]);
}
